/**
 * Admin Analytics Hooks
 *
 * Hooks de analíticas para el panel admin web (RPC vía dbClient del panel).
 * Uses the admin panel's dbClient (RPC-based) instead of the main app's db-client.
 *
 * RPCs used:
 * - get_analytics_dashboard(p_period, p_start_date?, p_end_date?)
 * - get_analytics_timeseries(p_metric, p_days?, p_start_date?, p_end_date?)
 * - get_analytics_income_timeseries(p_days?, p_start_date?, p_end_date?, p_granularity?)
 * - get_analytics_first_capture_conversion(p_days?, p_start_date?, p_end_date?, p_granularity?)
 * - get_analytics_capture_likes_summary(p_days?, p_start_date?, p_end_date?)
 * - get_analytics_capture_likes_avg_series(p_days?, p_start_date?, p_end_date?)
 * - get_engagement_funnel()
 * - get_cohort_retention(p_metric_type, p_days_after)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import type { ActivitySeriesMetricId } from '@/components/analytics/activitySeriesMetric';

// ─── Types ──────────────────────────────────────────────────────────────────

export type AnalyticsPeriod = 'day' | 'week' | 'month';

export interface AnalyticsMetrics {
    registered_users: number;
    active_users: number;
    total_captures: number;
    users_with_captures: number;
    first_capture_users: number;
    third_capture_users: number;
    fifth_capture_users: number;
    share_capture_count: number;
    share_capture_users: number;
    share_ratio: number;
    followers_created: number;
    users_following: number;
    users_following_5plus: number;
    users_following_10plus: number;
    level_page_views: number;
    level_page_users: number;
    material_page_views: number;
    material_page_users: number;
    quiz_sessions: number;
    quiz_users: number;
    app_shares: number;
    app_share_users: number;
    battles_count: number;
    battle_users: number;
    subscribers_active: number;
    subscribers_canceled: number;
    subscribers_accumulated: number;
    /** Altas de suscripción en el periodo seleccionado */
    subscribers_new_in_period?: number;
    /** Bajas / vencimientos en el periodo */
    subscribers_ended_in_period?: number;
    /** Snapshot: Pro de pago activos ahora */
    pro_paid_active?: number;
    pro_coupon_active?: number;
    pro_admin_active?: number;
    pro_gift_active?: number;
}

export interface DashboardData {
    period: AnalyticsPeriod;
    start_date: string;
    end_date: string;
    metrics: AnalyticsMetrics;
}

export interface TimeSeriesPoint {
    date: string;
    value: number;
}

export interface EngagementFunnel {
    total_users: number;
    users_1_capture: number;
    users_3_captures: number;
    users_5_captures: number;
    users_10_captures: number;
    conversion_rates: {
        signup_to_first: number;
        first_to_third: number;
        third_to_fifth: number;
        fifth_to_tenth: number;
    };
}

export interface CohortRetention {
    cohort_date: string;
    initial_count: number;
    day_1_pct: number | null;
    day_3_pct: number | null;
    day_5_pct: number | null;
    day_7_pct: number | null;
    day_10_pct: number | null;
    day_20_pct: number | null;
    day_30_pct: number | null;
}

/** Fecha de calendario en Madrid (YYYY-MM-DD). El día en curso de las series no se pinta. */
function madridTodayISO(): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date());
    const pick = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((part) => part.type === type)?.value ?? '';
    return `${pick('year')}-${pick('month')}-${pick('day')}`;
}

function withoutOpenDay<T extends { date: string }>(rows: T[]): T[] {
    const today = madridTodayISO();
    return rows.filter((row) => String(row.date ?? '').slice(0, 10) < today);
}

/** Postgres NUMERIC llega como string vía node-pg; normalizar antes de pintar %. */
function normalizeCohortRetentionRow(row: Record<string, unknown>): CohortRetention {
    const pct = (value: unknown): number | null => {
        if (value == null || value === '') return null;
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    };

    return {
        cohort_date: String(row.cohort_date ?? row.cohortDate ?? ''),
        initial_count: Number(row.initial_count ?? row.initialCount ?? 0),
        day_1_pct: pct(row.day_1_pct ?? row.day1Pct),
        day_3_pct: pct(row.day_3_pct ?? row.day3Pct),
        day_5_pct: pct(row.day_5_pct ?? row.day5Pct),
        day_7_pct: pct(row.day_7_pct ?? row.day7Pct),
        day_10_pct: pct(row.day_10_pct ?? row.day10Pct),
        day_20_pct: pct(row.day_20_pct ?? row.day20Pct),
        day_30_pct: pct(row.day_30_pct ?? row.day30Pct),
    };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useAnalyticsDashboard(
    period: AnalyticsPeriod = 'day',
    startDate?: Date,
    endDate?: Date,
) {
    return useQuery<DashboardData>({
        queryKey: ['admin', 'analytics-dashboard', period, startDate?.toISOString(), endDate?.toISOString()],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_analytics_dashboard', {
                p_period: period,
                p_start_date: startDate?.toISOString().split('T')[0] || null,
                p_end_date: endDate?.toISOString().split('T')[0] || null,
            });
            if (error) throw error;
            // Response can be array or object
            const normalized = normalizeRpcResponse(data);
            return (normalized[0] || data) as DashboardData;
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export type AnalyticsTimeSeriesMetric =
    | 'captures'
    | ActivitySeriesMetricId
    | 'quiz_sessions'
    | 'battles'
    | 'followers'
    | 'shares'
    | 'registered_users'
    | 'subscribers'
    | 'capture_likes'
    | 'epescar_patrols';

export interface CaptureLikesSummary {
    start_date: string;
    end_date: string;
    captures_created: number;
    likes_on_those_captures: number;
    likes_created_in_period: number;
    avg_likes_per_new_capture: number;
}

export function useAnalyticsTimeSeries(
    metric: AnalyticsTimeSeriesMetric,
    days: number | null = 30,
    startDate?: Date | null,
    endDate?: Date | null,
    enabled = true,
) {
    return useQuery<TimeSeriesPoint[]>({
        queryKey: ['admin', 'analytics-timeseries', metric, days, startDate?.toISOString(), endDate?.toISOString()],
        enabled,
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_analytics_timeseries', {
                p_metric: metric,
                p_days: days,
                p_start_date: startDate?.toISOString().split('T')[0] || null,
                p_end_date: endDate?.toISOString().split('T')[0] || null,
            });
            if (error) throw error;
            return withoutOpenDay(normalizeRpcResponse(data) as TimeSeriesPoint[]);
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

function normalizeCaptureLikesSummary(raw: unknown): CaptureLikesSummary {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const nested = o.get_analytics_capture_likes_summary;
    const src = (nested && typeof nested === 'object' ? nested : o) as Record<string, unknown>;
    const n = (v: unknown) => {
        const x = Number(v);
        return Number.isFinite(x) ? x : 0;
    };
    return {
        start_date: String(src.start_date ?? src.startDate ?? ''),
        end_date: String(src.end_date ?? src.endDate ?? ''),
        captures_created: n(src.captures_created ?? src.capturesCreated),
        likes_on_those_captures: n(src.likes_on_those_captures ?? src.likesOnThoseCaptures),
        likes_created_in_period: n(src.likes_created_in_period ?? src.likesCreatedInPeriod),
        avg_likes_per_new_capture: n(src.avg_likes_per_new_capture ?? src.avgLikesPerNewCapture),
    };
}

/** KPI Tendencias: likes en periodo + promedio por captura creada en el rango. */
export function useCaptureLikesSummary(
    days: number | null = 30,
    startDate?: Date | null,
    endDate?: Date | null,
) {
    return useQuery<CaptureLikesSummary>({
        queryKey: ['admin', 'capture-likes-summary', days, startDate?.toISOString(), endDate?.toISOString()],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_analytics_capture_likes_summary', {
                p_days: days,
                p_start_date: startDate?.toISOString().split('T')[0] || null,
                p_end_date: endDate?.toISOString().split('T')[0] || null,
            });
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            const row = Array.isArray(normalized) ? normalized[0] : normalized;
            return normalizeCaptureLikesSummary(row ?? data);
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export interface CaptureLikesAvgPoint {
    date: string;
    captures: number;
    likes: number;
    avgLikes: number | null;
}

function normalizeCaptureLikesAvgPoint(row: Record<string, unknown>): CaptureLikesAvgPoint {
    const n = (value: unknown) => {
        const x = Number(value);
        return Number.isFinite(x) ? x : 0;
    };
    const rawAvg = row.avg_likes ?? row.avgLikes;
    const avg = rawAvg == null || rawAvg === '' ? null : Number(rawAvg);
    const rawDate = row.date;
    const date = rawDate instanceof Date
        ? `${rawDate.getUTCFullYear()}-${String(rawDate.getUTCMonth() + 1).padStart(2, '0')}-${String(rawDate.getUTCDate()).padStart(2, '0')}`
        : String(rawDate ?? '').slice(0, 10);

    return {
        date,
        captures: n(row.captures),
        likes: n(row.likes),
        avgLikes: avg != null && Number.isFinite(avg) ? avg : null,
    };
}

/** Serie diaria: likes actuales de las capturas publicadas ese día / esas capturas. */
export function useCaptureLikesAvgSeries(
    days: number | null = 30,
    enabled = true,
) {
    return useQuery<CaptureLikesAvgPoint[]>({
        queryKey: ['admin', 'capture-likes-avg-series', days],
        enabled,
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_analytics_capture_likes_avg_series', {
                p_days: days,
                p_start_date: null,
                p_end_date: null,
            });
            if (error) throw error;
            return withoutOpenDay(
                normalizeRpcResponse(data).map((row) =>
                    normalizeCaptureLikesAvgPoint(row as Record<string, unknown>),
                ),
            );
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function useEngagementFunnel() {
    return useQuery<EngagementFunnel>({
        queryKey: ['admin', 'engagement-funnel'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_engagement_funnel');
            if (error) throw error;
            const normalized = normalizeRpcResponse(data);
            return (normalized[0] || data) as EngagementFunnel;
        },
        staleTime: 10 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export interface CommerceOutcomePoint {
    date: string;
    licenseSold: number;
    licensePending: number;
    storeSold: number;
    storePending: number;
}

function normalizeCommerceOutcome(row: Record<string, unknown>): CommerceOutcomePoint {
    const n = (value: unknown) => {
        const x = Number(value);
        return Number.isFinite(x) ? x : 0;
    };
    const rawDate = row.date;
    const date = rawDate instanceof Date
        ? `${rawDate.getUTCFullYear()}-${String(rawDate.getUTCMonth() + 1).padStart(2, '0')}-${String(rawDate.getUTCDate()).padStart(2, '0')}`
        : String(rawDate ?? '').slice(0, 10);

    return {
        date,
        licenseSold: n(row.license_sold ?? row.licenseSold),
        licensePending: n(row.license_pending ?? row.licensePending),
        storeSold: n(row.store_sold ?? row.storeSold),
        storePending: n(row.store_pending ?? row.storePending),
    };
}

/** Tendencias: licencias y pedidos de tienda vendidos vs pendientes de pago, por día. */
export function useCommerceOutcomeSeries(
    days: number | null = 30,
    startDate?: Date | null,
    endDate?: Date | null,
) {
    return useQuery<CommerceOutcomePoint[]>({
        queryKey: ['admin', 'commerce-outcomes', days, startDate?.toISOString(), endDate?.toISOString()],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_analytics_commerce_outcomes', {
                p_days: days,
                p_start_date: startDate?.toISOString().split('T')[0] || null,
                p_end_date: endDate?.toISOString().split('T')[0] || null,
            });
            if (error) throw error;
            return withoutOpenDay(
                normalizeRpcResponse(data).map((row) =>
                    normalizeCommerceOutcome(row as Record<string, unknown>)
                )
            );
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export type IncomeGranularity = 'day' | 'week' | 'month';
export type IncomeBusinessLineFilter = 'total' | 'subscription' | 'license' | 'store';

export interface IncomeTimeseriesPoint {
    periodStart: string;
    subscriptionIncomeCents: number;
    licenseIncomeCents: number;
    storeIncomeCents: number;
    totalIncomeCents: number;
}

function normalizeIncomePoint(row: Record<string, unknown>): IncomeTimeseriesPoint {
    const n = (value: unknown) => {
        const x = Number(value);
        return Number.isFinite(x) ? x : 0;
    };
    const rawDate = row.period_start ?? row.periodStart;
    const periodStart = rawDate instanceof Date
        ? `${rawDate.getUTCFullYear()}-${String(rawDate.getUTCMonth() + 1).padStart(2, '0')}-${String(rawDate.getUTCDate()).padStart(2, '0')}`
        : String(rawDate ?? '').slice(0, 10);

    return {
        periodStart,
        subscriptionIncomeCents: n(row.subscription_income_cents ?? row.subscriptionIncomeCents),
        licenseIncomeCents: n(row.license_income_cents ?? row.licenseIncomeCents),
        storeIncomeCents: n(row.store_income_cents ?? row.storeIncomeCents),
        totalIncomeCents: n(row.total_income_cents ?? row.totalIncomeCents),
    };
}

/** Tendencias: ingresos (base imponible) Suscripciones / Licencias / Tienda. */
export function useIncomeTimeseries(
    days: number | null = 30,
    granularity: IncomeGranularity = 'day',
) {
    return useQuery<IncomeTimeseriesPoint[]>({
        queryKey: ['admin', 'income-timeseries', days, granularity],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_analytics_income_timeseries', {
                p_days: days,
                p_start_date: null,
                p_end_date: null,
                p_granularity: granularity,
            });
            if (error) throw error;
            return normalizeRpcResponse(data).map((row) =>
                normalizeIncomePoint(row as Record<string, unknown>)
            );
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export interface FirstCaptureConversionPoint {
    periodStart: string;
    registered: number;
    firstCapture: number;
    conversionPct: number;
}

function normalizeFirstCaptureConversion(row: Record<string, unknown>): FirstCaptureConversionPoint {
    const n = (value: unknown) => {
        const x = Number(value);
        return Number.isFinite(x) ? x : 0;
    };
    const rawDate = row.period_start ?? row.periodStart;
    const periodStart = rawDate instanceof Date
        ? `${rawDate.getUTCFullYear()}-${String(rawDate.getUTCMonth() + 1).padStart(2, '0')}-${String(rawDate.getUTCDate()).padStart(2, '0')}`
        : String(rawDate ?? '').slice(0, 10);

    return {
        periodStart,
        registered: n(row.registered),
        firstCapture: n(row.first_capture ?? row.firstCapture),
        conversionPct: n(row.conversion_pct ?? row.conversionPct),
    };
}

/** Tendencias: % de cada cohorte de registros que publica una primera captura. */
export function useFirstCaptureConversion(
    days: number | null = 30,
    granularity: IncomeGranularity = 'week',
) {
    return useQuery<FirstCaptureConversionPoint[]>({
        queryKey: ['admin', 'first-capture-conversion', days, granularity],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_analytics_first_capture_conversion', {
                p_days: days,
                p_start_date: null,
                p_end_date: null,
                p_granularity: granularity,
            });
            if (error) throw error;
            return normalizeRpcResponse(data).map((row) =>
                normalizeFirstCaptureConversion(row as Record<string, unknown>)
            );
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export type CohortMetricType = 'login' | 'capture' | 'quiz' | 'battle';
export type CohortActivity = 'session' | 'capture' | 'feed' | 'any';

export interface CohortWeekRetention {
    cohort_date: string;
    initial_count: number;
    w0_pct: number | null;
    w1_pct: number | null;
    w2_pct: number | null;
    w3_pct: number | null;
    w4_pct: number | null;
    w6_pct: number | null;
    w8_pct: number | null;
    w12_pct: number | null;
    weeks_0_pct: number | null;
    weeks_1_pct: number | null;
    weeks_2_pct: number | null;
    weeks_3_4_pct: number | null;
    weeks_5plus_pct: number | null;
    consecutive_pct: number | null;
    gapped_pct: number | null;
}

export function normalizeCohortWeekRow(row: Record<string, unknown>): CohortWeekRetention {
    const pct = (value: unknown): number | null => {
        if (value == null || value === '') return null;
        const n = Number(value);
        if (!Number.isFinite(n) || n < 0 || n > 100) return null;
        return n;
    };
    return {
        cohort_date: String(row.cohort_date ?? '').slice(0, 10),
        initial_count: Number(row.initial_count ?? 0),
        w0_pct: pct(row.w0_pct),
        w1_pct: pct(row.w1_pct),
        w2_pct: pct(row.w2_pct),
        w3_pct: pct(row.w3_pct),
        w4_pct: pct(row.w4_pct),
        w6_pct: pct(row.w6_pct),
        w8_pct: pct(row.w8_pct),
        w12_pct: pct(row.w12_pct),
        weeks_0_pct: pct(row.weeks_0_pct),
        weeks_1_pct: pct(row.weeks_1_pct),
        weeks_2_pct: pct(row.weeks_2_pct),
        weeks_3_4_pct: pct(row.weeks_3_4_pct),
        weeks_5plus_pct: pct(row.weeks_5plus_pct),
        consecutive_pct: pct(row.consecutive_pct),
        gapped_pct: pct(row.gapped_pct),
    };
}

/** 150 días: W8 y W12 pueden haber cerrado. El alta es la misma regla que la vista por días. */
export function useCohortRetentionWeeks(
    metricType: CohortMetricType,
    activity: CohortActivity = 'session',
    daysAfter = 150,
    enabled = true,
) {
    return useQuery<CohortWeekRetention[]>({
        queryKey: ['admin', 'cohort-retention-weeks', metricType, activity, daysAfter],
        enabled,
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_cohort_retention_weeks', {
                p_metric_type: metricType,
                p_activity: activity,
                p_days_after: daysAfter,
            });
            if (error) throw error;
            return normalizeRpcResponse(data).map((row) =>
                normalizeCohortWeekRow(row as Record<string, unknown>)
            );
        },
        staleTime: 10 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function useCohortRetention(
    metricType: 'login' | 'capture' | 'quiz' | 'battle',
    daysAfter = 90,
) {
    return useQuery<CohortRetention[]>({
        queryKey: ['admin', 'cohort-retention', metricType, daysAfter],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_cohort_retention', {
                p_metric_type: metricType,
                p_days_after: daysAfter,
            });
            if (error) throw error;
            return normalizeRpcResponse(data).map((row) =>
                normalizeCohortRetentionRow(row as Record<string, unknown>)
            );
        },
        staleTime: 10 * 60_000,
        refetchOnWindowFocus: false,
    });
}
